/**
 * Project analysis from the GUI: the folder to scan, the scan itself (`analyze.scan`), a
 * repository scanned by its address (`repos.scan`, with the access tokens the core keeps), and the
 * last report, kept here so it survives the Analyze window being moved or closed.
 *
 * A scan merges into the graph as one edit (one Ctrl+Z takes it back); the graph mirror learns
 * about it from the usual `graph.changed` event. Nodes the code no longer has ("vanished") are
 * only listed: removing them is the user's choice, in one edit too.
 */
import { createSignal, type Accessor } from "solid-js";
import type { AnalysisReport } from "@protocol/AnalysisReport";
import type { RepoAccess } from "@protocol/RepoAccess";
import type { RepoSource } from "@protocol/RepoSource";
import type { Actions } from "../client/actions";
import { isClientError, type Client } from "../client/client";
import type { GraphMirror } from "../client/graph";
import { readFileList, readHandle, type DirectoryHandle, type PickedFolder } from "../shared/folders";
import { load, save } from "../shared/storage";
import { toast } from "../shared/toast";
import type { MenuSection, ToolbarButton } from "../shared/menus";

/** Where the last scanned folder is remembered in this browser. */
const ROOT_KEY = "analyze.root";

/** Where the last scanned repository (address, branch, folder) is remembered in this browser. */
const REPO_KEY = "analyze.repo";

/** What the service needs. */
export interface AnalysisDeps {
  client: Client;
  graph: GraphMirror;
  actions: Actions;
  /** Whether this core reads folders on its disk (not the standalone browser build). */
  readsFolders: boolean;
  /** Asks for a folder with the system's dialog (desktop app only). */
  pickFolder?: () => Promise<string | null>;
  /** The browser's folder picker (`window.showDirectoryPicker`), where there is one. */
  directoryPicker?: () => Promise<DirectoryHandle>;
}

/** A folder picked in the page: its name, and how to read its files (again). */
interface PickedSource {
  name: string;
  read(): Promise<PickedFolder>;
}

/** The analysis service. */
export interface Analysis {
  /** Whether the core reads the folder itself ({@link Analysis.root}); otherwise the page picks and reads it. */
  readsFolders: boolean;
  /** Whether the page can ask for a folder with the browser's own picker (else: a file input). */
  canPickInPage: boolean;
  /** The name of the folder picked in the page, if one was. */
  picked: Accessor<string | null>;
  /** Asks for a folder with the browser's picker, then scans it. */
  pickAndScan(): Promise<boolean>;
  /** Scans the files of an `<input webkitdirectory>`. */
  scanFileList(files: ArrayLike<File>): Promise<boolean>;
  /** The folder to scan, as typed or picked (core with a disk). */
  root: Accessor<string>;
  setRoot(root: string): void;
  /** `true` while a scan runs. */
  running: Accessor<boolean>;
  /** The last scan's report. */
  report: Accessor<AnalysisReport | null>;
  /** Why the last scan failed, if it did. */
  error: Accessor<string | null>;
  /** Which kind of scan {@link Analysis.error} is about: a folder's or a repository's. */
  errorFrom: Accessor<"folder" | "repo">;
  /** Scans {@link Analysis.root}, or the folder picked in the page; resolves with `true` when the graph was updated. */
  scan(): Promise<boolean>;
  /** The vanished nodes that are still in the graph. */
  vanished(): string[];
  /** Removes the vanished nodes (one undoable edit). */
  removeVanished(): Promise<boolean>;
  /** Asks for a folder with the system's dialog; `undefined` where there is none. */
  browse?: () => Promise<void>;
  /** The repository to scan by its address (`repos.scan`): address, branch and folder as typed. */
  repo: Accessor<RepoSource>;
  setRepo(change: Partial<RepoSource>): void;
  /** Fetches {@link Analysis.repo} and scans it; resolves with `true` when the graph was updated. */
  scanRepo(): Promise<boolean>;
  /** How the core may get into repositories (`null` until asked, or when it cannot say). */
  repoAccess: Accessor<RepoAccess | null>;
  /** Asks the core again (after plugins changed). */
  refreshAccess(): Promise<void>;
  /** Keeps the access token for a host, or forgets it with an empty token; resolves with `true` when done. */
  setRepoToken(host: string, token: string): Promise<boolean>;
}

/** The typed source as sent: trimmed, empty branch and folder left out. */
export function repoRequest(typed: RepoSource): RepoSource | null {
  const url = typed.url.trim();
  if (!url) return null;
  const branch = typed.branch?.trim();
  const folder = typed.folder?.trim().replace(/^\/+|\/+$/g, "");
  return { url, ...(branch ? { branch } : {}), ...(folder ? { folder } : {}) };
}

/** Creates the analysis service. */
export function createAnalysis(deps: AnalysisDeps): Analysis {
  const [root, setRootSignal] = createSignal(load<string>(ROOT_KEY, ""));
  const [running, setRunning] = createSignal(false);
  const [report, setReport] = createSignal<AnalysisReport | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [errorFrom, setErrorFrom] = createSignal<"folder" | "repo">("folder");
  const [source, setSource] = createSignal<PickedSource | null>(null);
  const [repo, setRepoSignal] = createSignal<RepoSource>(load<RepoSource>(REPO_KEY, { url: "" }));
  const [repoAccess, setRepoAccess] = createSignal<RepoAccess | null>(null);

  /** The workspace the page works in, which a scan goes into (none: the environment's first). */
  const into = () => {
    const workspace = deps.graph.scope?.()?.workspace;
    return workspace ? { workspace } : {};
  };

  /** Changes the folder and remembers it. */
  const setRoot = (value: string) => {
    setRootSignal(value);
    save(ROOT_KEY, value);
  };

  /** The vanished nodes still in the graph (the user may have removed some already). */
  const vanished = () => (report()?.vanished ?? []).filter((id) => deps.graph.state.graph.nodes.some((n) => n.id === id));

  /** Runs one scan request, keeping its report or why it failed. */
  const run = async (request: Parameters<Client["request"]>[0]) => {
    setRunning(true);
    setError(null);
    setErrorFrom(request.method === "repos.scan" ? "repo" : "folder");
    try {
      const response = await deps.client.request(request);
      if (response.type !== "analysis") return false;
      setReport(response.report);
      const r = response.report;
      toast(`Scanned ${r.files} files: ${r.added.length} added, ${r.updated.length} updated. Press Ctrl+Z to undo.`);
      return true;
    } catch (e) {
      setError(isClientError(e) ? e.message : String(e));
      return false;
    } finally {
      setRunning(false);
    }
  };

  /** Scans the folder path (the core reads it). */
  const scanFolder = async () => {
    const folder = root().trim();
    if (!folder || running()) return false;
    return run({ method: "analyze.scan", params: { root: folder, ...into() } });
  };

  /** Reads the folder picked in the page and sends its files. */
  const scanPicked = async () => {
    const picked = source();
    if (!picked || running()) return false;
    let folder: PickedFolder;
    try {
      folder = await picked.read();
    } catch (e) {
      setErrorFrom("folder");
      setError(`cannot read the folder: ${String(e)}`);
      return false;
    }
    if (!folder.files.length) {
      setErrorFrom("folder");
      setError(`nothing to read in ${folder.name}`);
      return false;
    }
    return run({ method: "analyze.scan_files", params: { name: folder.name, files: folder.files, ...into() } });
  };

  /** Asks the core how it may get into repositories; an older core or a refusal leaves `null`. */
  const refreshAccess = async () => {
    try {
      const response = await deps.client.request({ method: "repos.access" });
      setRepoAccess(response.type === "repo_access" ? response.access : null);
    } catch {
      setRepoAccess(null);
    }
  };

  return {
    readsFolders: deps.readsFolders,
    repo,
    setRepo(change) {
      const next = { ...repo(), ...change };
      setRepoSignal(next);
      save(REPO_KEY, next);
    },
    async scanRepo() {
      const wanted = repoRequest(repo());
      if (!wanted || running()) return false;
      return run({ method: "repos.scan", params: { source: wanted, ...into() } });
    },
    repoAccess,
    refreshAccess,
    async setRepoToken(host, token) {
      try {
        const response = await deps.client.request({ method: "repos.set_token", params: { host: host.trim(), token: token.trim() } });
        if (response.type === "repo_access") setRepoAccess(response.access);
        toast(token.trim() ? `Access token kept for ${host.trim().toLowerCase()}.` : `Access token for ${host.trim()} forgotten.`);
        return true;
      } catch (e) {
        toast(isClientError(e) ? e.message : String(e), "error");
        return false;
      }
    },
    root,
    setRoot,
    running,
    report,
    error,
    errorFrom,
    vanished,
    canPickInPage: !!deps.directoryPicker,
    picked: () => source()?.name ?? null,
    scan: () => (deps.readsFolders ? scanFolder() : scanPicked()),
    async pickAndScan() {
      if (!deps.directoryPicker || running()) return false;
      let handle: DirectoryHandle;
      try {
        handle = await deps.directoryPicker();
      } catch {
        return false; // the user closed the picker
      }
      setSource({ name: handle.name, read: () => readHandle(handle) });
      return scanPicked();
    },
    async scanFileList(files) {
      if (!files.length || running()) return false;
      const folder = await readFileList(files);
      setSource({ name: folder.name, read: async () => folder });
      return scanPicked();
    },
    async removeVanished() {
      const ids = vanished();
      if (!ids.length) return false;
      const changes = await deps.actions.removeNodes(ids);
      if (!changes) return false;
      toast(`${ids.length} node${ids.length === 1 ? "" : "s"} removed. Press Ctrl+Z to undo.`);
      return true;
    },
    browse: deps.pickFolder
      ? async () => {
          const folder = await deps.pickFolder!();
          if (folder) setRoot(folder);
        }
      : undefined,
  };
}

/** The Tools menu's code reading. */
export function analysisMenus(): MenuSection[] {
  return [{ menu: "tools", section: "Code", order: 10, entries: () => [{ label: "Analyze a project…", command: "window.open", argument: "analyze" }] }];
}

/** The toolbar's Analyze. */
export function analysisToolbar(): ToolbarButton[] {
  return [{ id: "tool-analyze", section: "tools", order: 40, icon: "scan", label: "Analyze", title: "Read a project's code into the graph", command: "window.open", argument: "analyze" }];
}
